/* eslint-disable */
import React from 'react';
import { useTranslation } from 'react-i18next';
import './UserProfile.css';
import ProductSettingsDrawer from '../components/ProductSettingsDrawer/ProductSettingsDrawer';
import { useAuthStore } from '../../store/useAuthStore';
import { useNavigate } from 'react-router-dom';
import CertificatesList from '../../components/CertificatesList/CertificatesList';
import CertificatePopup from '../../components/CertificatePopup/CertificatePopup';
import Avatar from '../../components/Avatar/Avatar';
import {Certificate, getMyCertificates} from "../../api/certificateService";
import {getCurrentUser} from "../../api/productService";
import {ViewWrapper} from "../../components/ViewWrapper/ViewWrapper";
import {useDisplayMode} from "../../hooks/useDisplayMode";
import CopyrightText from "../components/CopyrightText/CopyrightText";

const certificatesTest: Certificate[] = [
  {
    id: 1,
    name: "ChatGPT",
    issueDate: "2024-03-15",
    courseId: "chat-gpt",
    courseName: "React Development Basics",
    certificateUrl: "https://example.com/certificates/react-fundamentals.pdf"
  },
  {
    id: 2,
    name: "MidJourney",
    issueDate: "2024-05-22",
    courseId: "mid-journey",
    courseName: "TypeScript Mastery Course"
  },
  {
    id: 3,
    name: "Mistral",
    issueDate: "2024-07-10",
    courseId: "mistral",
    courseName: "Full Stack Node.js Development",
    certificateUrl: "https://example.com/certificates/nodejs-backend.pdf"
  },
  {
    id: 4,
    name: "Grok",
    issueDate: "2024-08-18",
    courseId: "grok",
    courseName: "Advanced Web Performance Techniques"
  }
];


const UserProfile = () => {
  const { t } = useTranslation();
  const logout = useAuthStore(state => state.logout);
  const navigate = useNavigate();
  const [user, setUser] = React.useState<{ name: string; email: string } | null>(null);
  const [certificates, setCertificates] = React.useState<Certificate[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedCertificate, setSelectedCertificate] = React.useState<Certificate | null>(null);
  const [popupOpen, setPopupOpen] = React.useState(false);
  const {isDesktop, isMobile} = useDisplayMode();

  const handleCertificateClick = (certificate: Certificate) => {
    setSelectedCertificate(certificate);
    setPopupOpen(true);
  };

  const handleClosePopup = () => {
    setPopupOpen(false);
    setSelectedCertificate(null);
  };

  const handleGetCertificate = (certificate: Certificate) => {
    if (certificate.certificateUrl) {
      window.open(certificate.certificateUrl, '_blank');
    }
    handleClosePopup();
  };

  React.useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch user and certificates in parallel
        const [{user}, certificatesData] = await Promise.all([
          getCurrentUser(),
          getMyCertificates()
        ]);

        // Check if PIN is set up, redirect if not
        const apiUser = user;
        if (!apiUser.isPinSetUp) {
          navigate('/pin');
          return;
        }

        // Map API response to local user object with name and email
        setUser({
          name: apiUser.username || apiUser.email || 'User',
          email: apiUser.email || '',
        });

        const preparedCert = certificatesData?.data.map((item: any) => {
          return {
            id: item.id,
            name:item?.course?.title,
            issueDate: item.publishedAt,
            courseId: "grok",
            certNumber: item?.certificateNumber,
            courseName: "Advanced Web Performance Techniques"
          }
        })
        setCertificates(preparedCert);
      } catch (err: any) {
        setError(t('product.profile.error'));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [t, navigate]);

  if (loading) {
    return <div className="user-profile-bg"><div className="user-profile-container">{t('product.profile.loading')}</div></div>;
  }
  if (error) {
    return <div className="user-profile-bg"><div className="user-profile-container">{error}</div></div>;
  }
  if (!user) return null;

  return (
    <div className="user-profile-bg">

      <ViewWrapper enable={isMobile}>
        {/* Custom Mobile Header */}
        <div className="user-profile-header">
          <div className="user-profile-header-left" />
          <div className="user-profile-header-title">{t('product.profile.title')}</div>
          <button className="user-profile-header-settings" aria-label={t('product.settings.title')} onClick={() => setDrawerOpen(true)}>
            {/* Settings Icon */}
            <img src="/images/ui/settings.png" alt={t('product.settings.title')} width={24} height={24} />
          </button>
        </div>
      </ViewWrapper>
      <ProductSettingsDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />

      <div className="user-profile-container">
        {/* User Card */}
        <div className="user-profile-card">
          <Avatar user_name={user.name} />
          <div>
            <div className="user-profile-name">{user.name}</div>
            <div className="user-profile-email">{user.email}</div>
          </div>
        </div>

        <ViewWrapper enable={isDesktop}>
          <div className="user-profile-help" onClick={() => setDrawerOpen(true)} >
            <div className="user-profile-help-left">
                <img src="/images/ui/settings.png" alt={t('product.settings.title')} width={24} height={24} />
              {t('product.profile.title')}
            </div>
            <svg width="20" height="20" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 7l5 5-5 5" /></svg>
          </div>
        </ViewWrapper>


        {/* Help Button */}
        <div className="user-profile-help"  onClick={() => {
         navigate('/help')
        }}>
          <div className="user-profile-help-left">
            <img src="/images/ui/circle-help.png" alt={t('product.profile.help')} width={20} height={20} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            {t('product.profile.help')}
          </div>
          <svg width="20" height="20" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 7l5 5-5 5" /></svg>
        </div>

        {/* Streak Details */}
        <div className="user-profile-streak-title">{t('product.profile.streak.title')}</div>
        <div className="user-profile-streak-row">
          <div className="user-profile-streak-card">
            <div className="user-profile-streak-label">{t('product.profile.streak.current')}</div>
            <div className="user-profile-streak-value">0</div>
          </div>
          <div className="user-profile-streak-card">
            <div className="user-profile-streak-label">{t('product.profile.streak.longest')}</div>
            <div className="user-profile-streak-value">3</div>
          </div>
        </div>

        {/* Certificates */}
        <div className="user-profile-cert-title">{t('product.profile.certificates')}</div>
        <div className="user-profile-cert-list">
          {certificates?.length === 0 ? (
            <div className="no-certificates-block">
              {/*<img src="/images/ui/files.png" alt={t('product.profile.noCertificates.title')} className="no-certificates-icon" />*/}
              <div className="no-certificates-title">{t('product.profile.noCourses.title')}</div>
              <div className="no-certificates-subtitle">{t('product.profile.noCourses.subtitle')}</div>
              <button className="no-certificates-button" onClick={() => navigate('/courses')}>
                {t('product.profile.noCourses.button')}
              </button>
            </div>
          ) : (
            <CertificatesList
              items={certificates}
              onItemClick={handleCertificateClick}
            />
          )}
        </div>

        {/* Logout Row */}
        <div
          className="user-profile-help user-profile-logout-row"
          style={{ marginTop: 24, marginBottom: 24 }}
          onClick={logout}
          role="button"
          tabIndex={0}
        >
          <div className="user-profile-help-left user-profile-logout">
            <img src="/images/ui/log-out.png" alt={t('product.profile.logOut')} width={20} height={20} style={{ marginRight: 6, verticalAlign: 'middle' }} />
            {t('product.profile.logOut')}
          </div>
        </div>
        <CopyrightText />
      </div>

      <CertificatePopup
        certificate={selectedCertificate}
        isOpen={popupOpen}
        onClose={handleClosePopup}
        onGetCertificate={handleGetCertificate}
      />
    </div>
  );
};

export default UserProfile;
